"use client";

import { WP_Query } from "@/integrations/wordpress/wp_query";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function RareCollectionGrid() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="aspect-[4/5] border border-steel-grey/20 bg-ink-black animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-steel-grey flex items-center gap-2">
        Unable to load prints.
        <Button onClick={refetch} variant="link" className="text-fusion-cyan underline p-0 h-auto">
          Retry
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="font-mono text-sm tracking-wider uppercase text-steel-grey">
        No prints currently in the catalog.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => {
      const printNumber = String(product.id).padStart(4, "0");
      return <div key={product.id} data-index={i} className="group relative border border-steel-grey/20 bg-ink-black p-5 transition-colors duration-300 hover:border-fusion-cyan">
            <span aria-hidden="true" className="pointer-events-none absolute top-2 left-2 h-2 w-2 border-t border-l border-steel-grey" />
            <span aria-hidden="true" className="pointer-events-none absolute top-2 right-2 h-2 w-2 border-t border-r border-steel-grey" />
            <span aria-hidden="true" className="pointer-events-none absolute bottom-2 left-2 h-2 w-2 border-b border-l border-steel-grey" />
            <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 h-2 w-2 border-b border-r border-steel-grey transition-colors duration-300 group-hover:border-fusion-cyan" />

            <Link to={product.link} className="block">
              <div className="relative overflow-hidden bg-steel-grey/10 aspect-[4/5]">
                {product.featured_image_url && <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />}

                <Badge variant="outline" className="absolute top-3 left-3 border-steel-grey bg-transparent text-white font-mono text-[10px] tracking-[0.18em] uppercase rounded-lg px-2.5 py-1">
                  Premium
                </Badge>

                <span className="absolute top-3 right-3 font-mono text-[10px] tracking-[0.08em] text-steel-grey">
                  N° {printNumber}
                </span>
              </div>

              <div className="mt-5 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-default text-lg font-semibold text-white leading-tight truncate">
                    {product.title}
                  </h3>
                  {product.categories && product.categories.length > 0 && <p className="mt-1 font-default italic text-[13px] text-steel-grey truncate">
                      {product.categories[0].name}
                    </p>}
                </div>
                {product.price !== undefined && <span className="shrink-0 font-mono text-sm tracking-[0.04em] text-fusion-cyan">
                    ${product.price}
                  </span>}
              </div>
            </Link>

            <div className="mt-5 pt-4 border-t border-steel-grey/20">
              <AddToCartButton productId={product.id} variant="outline" size="sm" className="w-full" buttonClassName="bg-fusion-cyan text-obsidian w-full border border-fusion-cyan hover:bg-transparent hover:text-fusion-cyan rounded-lg font-bold text-[12px] tracking-[0.18em] uppercase" text="Initialize Order" showQuantity={false} />
            </div>
          </div>;
    })}
    </div>;
}
export default function FeaturedPrints() {
  const wp_query_params = {
    per_page: 4,
    orderby: "date",
    order: "desc" as const,
    product_cat: [16]
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="2150" id="featured-print-collections" className="relative overflow-hidden bg-obsidian py-24 md:py-32">
      <span aria-hidden="true" className="absolute top-0 left-0 right-0 h-px bg-steel-grey/20" />

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-10 lg:px-[4.5rem]">
        <span aria-hidden="true" className="pointer-events-none absolute -top-8 right-4 md:right-10 font-default select-none text-white/05 leading-none" style={{
        fontSize: "clamp(12rem, 22vw, 22rem)",
        fontWeight: 800
      }}>
          03
        </span>

        <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="block h-px w-6 bg-fusion-cyan" />
              <span className="font-mono text-[12px] md:text-[13px] font-medium tracking-[0.18em] uppercase text-fusion-cyan">
                § N° 03 — Featured Collections
              </span>
            </div>

            <h2 className="mt-8 font-default text-primary-foreground" style={{
            fontSize: "clamp(2.25rem, 4.2vw, 4.25rem)",
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 1.0
          }}>
              Precision transfers for{" "}
              <span className="relative inline-block">
                <em className="font-default not-italic">
                  modern apparel
                </em>
                <span aria-hidden="true" className="absolute -bottom-1 left-0 right-3 flex items-center">
                  <span className="block h-px flex-1 bg-fusion-cyan" />
                  <span className="block h-1.5 w-1.5 rounded-lg bg-fusion-cyan" />
                </span>
              </span>{" "}
              production.
            </h2>
          </div>

          <div className="lg:col-span-4 lg:pt-4 flex flex-col justify-between gap-8">
            <p className="font-default text-primary" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
            lineHeight: 1.6
          }}>
              High-fidelity DTF prints engineered for durability, vibrant color accuracy, and rapid turnaround for your brand.
            </p>

            <Link to="#" className="group inline-flex items-center gap-3 font-mono text-[13px] font-bold tracking-[0.18em] uppercase text-white w-fit">
              <span className="relative pb-1 text-primary">
                Explore Catalog
                <span aria-hidden="true" className="absolute bottom-0 left-0 h-px w-3 bg-white transition-all duration-[280ms] ease-out group-hover:w-full" />
                <span aria-hidden="true" className="absolute bottom-0 left-0 h-1.5 w-1.5 -translate-x-1 translate-y-[3px] rounded-lg bg-fusion-cyan opacity-0 transition-opacity duration-[280ms] group-hover:opacity-100" />
              </span>
              <ArrowRight className="h-4 w-4 transition-transform duration-[280ms] group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <div className="relative mt-16 md:mt-20">
          <EcommerceProductsProvider wp_query={wp_query}>
            <RareCollectionGrid />
          </EcommerceProductsProvider>
        </div>
      </div>
    </section>;
}